// src/pages/outsourcing_management/requisitions/RequisitionPromptDialog.tsx
//
// EE-207 Phase 4 (#14) - the prompt in front of every Requisition workflow
// action that asks for one: Reject, Close and Cancel. All three ask the same
// question in the same box - a comment, then confirm - and Cancel adds one
// thing to it, a mandatory reason code. They were two components, so the
// comment field, its required/error handling and the submitting state were
// written twice and had already drifted (one cleared its state on close, both
// worded the helper text differently).
//
// Reject requires a non-empty comment (mirrors
// RequisitionWorkflowController.reject's 400 check); Close treats it as
// optional context. With `reason` given, the reason list comes from
// useRequisitionOptions - never hardcoded - and the comment becomes required
// when the picked reason has requiresComment=true (e.g. "Other" for either
// audience), mirroring RequisitionWorkflowController.cancel.
import { FunctionComponent, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListSubheader,
  MenuItem,
  TextField,
} from "@mui/material";
import { RequisitionOptionsType } from "types/RequisitionTypes";
import { groupCancellationReasonsByAudience } from "./requisitionWorkflow";

type ReasonConfig = {
  options?: RequisitionOptionsType;
  label: string;
  missingText: string;
};

type PropsType = {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Ignored when `reason` is given - the reason decides there. */
  commentRequired?: boolean;
  isSubmitting?: boolean;
  /** Cancel is destructive and says so. */
  destructive?: boolean;
  cancelLabel?: string;
  /** Present only for the actions that ask why. */
  reason?: ReasonConfig;
  onClose: () => void;
  onConfirm: (comment: string, reasonCode?: string) => void;
};

const RequisitionPromptDialog: FunctionComponent<PropsType> = ({
  open,
  title,
  description,
  confirmLabel,
  commentRequired,
  isSubmitting,
  destructive,
  cancelLabel = "Cancel",
  reason,
  onClose,
  onConfirm,
}) => {
  const [reasonCode, setReasonCode] = useState("");
  const [comment, setComment] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const { requester, supplyChain } = groupCancellationReasonsByAudience(
    reason?.options
  );
  const selectedReason = reason?.options?.cancellationReasons.find(
    (r) => r.code === reasonCode
  );
  const needsComment = reason
    ? !!selectedReason?.requiresComment
    : !!commentRequired;

  const reasonMissing = !!reason && showErrors && !reasonCode;
  const commentMissing = showErrors && needsComment && !comment.trim();

  const handleClose = () => {
    setReasonCode("");
    setComment("");
    setShowErrors(false);
    onClose();
  };

  const handleConfirm = () => {
    if ((reason && !reasonCode) || (needsComment && !comment.trim())) {
      setShowErrors(true);
      return;
    }
    onConfirm(comment.trim(), reason ? reasonCode : undefined);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        )}
        {reason && (
          <TextField
            select
            fullWidth
            required
            sx={{ mb: 2 }}
            label={reason.label}
            value={reasonCode}
            onChange={(e) => {
              setReasonCode(e.target.value);
              if (showErrors) setShowErrors(false);
            }}
            error={reasonMissing}
            helperText={reasonMissing ? reason.missingText : undefined}
          >
            <MenuItem value="">Select...</MenuItem>
            {requester.length > 0 && <ListSubheader>Requester</ListSubheader>}
            {requester.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
            {supplyChain.length > 0 && (
              <ListSubheader>Supply Chain</ListSubheader>
            )}
            {supplyChain.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          autoFocus={!reason}
          fullWidth
          multiline
          minRows={3}
          label={needsComment ? "Comment (required)" : "Comment (optional)"}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (showErrors) setShowErrors(false);
          }}
          error={commentMissing}
          helperText={
            commentMissing
              ? reason
                ? "A comment is required for this reason"
                : "A comment is required for this action"
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={destructive ? "error" : "primary"}
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequisitionPromptDialog;
